import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { getTenantLogoUrl, getTenantBackgroundUrl } from "../../services/tenantService";
import {
  getTenant,
  createTenant,
  patchTenant,
  deleteTenant,
  updateTenantPublic,
  updateTenantPublicWithFiles,
  activateTenant,
  deactivateTenant,
  getTenantStats,
  getTenantLogs,
  setTenantTrialExpiration
} from "../../services/tenantSuperadminService";
import { getAnonymizationRunStats } from "../../services/anonymizationRunsService";


const emptyForm = {
  name: "",
  slug: "",
  description: "",

  address: "",
  phone: "",
  email: "",

  ico: "",
  dic: "",
  ic_dph: "",
  account_type: "production",
  payment_method: "",
  is_active: true,

  anon_validation: false, 
  anon_dicom: false,

  first_login_at: null,
  trial_expires_at: null,
  deactivated_at: null,
  deactivation_reason: "",

  contact_company_address: "",
  contact_company_postal_code: "",
  contact_company_town: "",
  contact_company_country: "",

  contact_sales_first_name: "",
  contact_sales_last_name: "",
  contact_sales_phone: "",
  contact_sales_email: "",

  contact_admin_first_name: "",
  contact_admin_last_name: "",
  contact_admin_phone: "",
  contact_admin_email: "",

  admin_password: "",
  admin_password2: "",
};

export default function useTenantModalController({ open, tenantId, onClose, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const isEdit = !!tenantId;

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState({ logo: null, bg: null });
  const [preview, setPreview] = useState({ logo: null, bg: null });

  const adminUsername = useMemo(() => {
    const slug = (form.slug || "").trim();
    return slug ? `${slug}_admin` : "";
  }, [form.slug]);

  const toYyyyMmDd = (d) => d.toISOString().slice(0, 10);

  const makeCreatedAtGte = (days) => {
    const dt = new Date(Date.now() - Number(days || 0) * 24 * 60 * 60 * 1000);
    return toYyyyMmDd(dt);
  };

  // cleanup blob previews
  useEffect(() => {
    return () => {
      Object.values(preview).forEach((u) => u?.startsWith("blob:") && URL.revokeObjectURL(u));
    };
  }, [preview]);

  // init on open
  useEffect(() => {
    if (!open) return;

    setTab(0);
    setConfirmDelete(false);

    if (!isEdit) {
      setForm(emptyForm);
      setFiles({ logo: null, bg: null });
      setPreview({ logo: null, bg: null });
      return;
    }

    const run = async () => {
      setLoading(true);
      try {
        const tt = await getTenant(tenantId);

        setForm((prev) => ({
          ...prev,
          name: tt.name || "",
          slug: tt.slug || "",
          description: tt.description || "",

          address: tt.address || "",
          phone: tt.phone || "",
          email: tt.email || "",

          ico: tt.ico || "",
          dic: tt.dic || "",
          ic_dph: tt.ic_dph || "",
          account_type: tt.account_type || "production",
          payment_method: tt.payment_method ?? "",
          
          // --- Flags ---
          is_active: !!tt.is_active,
          anon_validation: !!tt.anon_validation, // Add this
          anon_dicom: !!tt.anon_dicom,           // Add this

          first_login_at: tt.first_login_at ?? null,
          trial_expires_at: tt.trial_expires_at ?? null,
          deactivated_at: tt.deactivated_at ?? null,
          deactivation_reason: tt.deactivation_reason || "",

          contact_company_address: tt.contact_company_address || "",
          contact_company_postal_code: tt.contact_company_postal_code || "",
          contact_company_town: tt.contact_company_town || "",
          contact_company_country: tt.contact_company_country || "",

          contact_sales_first_name: tt.contact_sales_first_name || "",
          contact_sales_last_name: tt.contact_sales_last_name || "",
          contact_sales_phone: tt.contact_sales_phone || "",
          contact_sales_email: tt.contact_sales_email || "",

          contact_admin_first_name: tt.contact_admin_first_name || "",
          contact_admin_last_name: tt.contact_admin_last_name || "",
          contact_admin_phone: tt.contact_admin_phone || "",
          contact_admin_email: tt.contact_admin_email || "",

          admin_password: "",
          admin_password2: "",
        }));

        setFiles({ logo: null, bg: null });
        setPreview({
          logo: getTenantLogoUrl(tt.id),
          bg: getTenantBackgroundUrl(tt.id),
        });
      } catch (e) {
        enqueueSnackbar(
          t("tenantModal.snackbar.failedToLoadTenant", "Nepodarilo sa načítať organizáciu."),
          { variant: "error" }
        );
        onClose?.();
      } finally {
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantId]);

  const onPick = (name, file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);

    if (name === "logo_file") {
      preview.logo?.startsWith("blob:") && URL.revokeObjectURL(preview.logo);
      setFiles((p) => ({ ...p, logo: file }));
      setPreview((p) => ({ ...p, logo: url }));
    } else {
      preview.bg?.startsWith("blob:") && URL.revokeObjectURL(preview.bg);
      setFiles((p) => ({ ...p, bg: file }));
      setPreview((p) => ({ ...p, bg: url }));
    }
  };

  const removeImg = async (key) => {
    if (!tenantId) return;
    setSaving(true);
    try {
      await updateTenantPublic(
        tenantId,
        key === "logo" ? { clear_logo: true } : { clear_login_photo: true }
      );

      preview[key]?.startsWith("blob:") && URL.revokeObjectURL(preview[key]);
      setFiles((p) => ({ ...p, [key]: null }));
      setPreview((p) => ({ ...p, [key]: null }));

      enqueueSnackbar(
        key === "logo"
          ? t("tenantModal.snackbar.logoRemoved", "Logo bolo odstránené.")
          : t("tenantModal.snackbar.backgroundRemoved", "Pozadie bolo odstránené."),
        { variant: "success" }
      );
      onChanged?.();
    } catch (e) {
      enqueueSnackbar(
        t("tenantModal.snackbar.failedToRemoveImage", "Nepodarilo sa odstrániť obrázok."),
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async () => {
    if (!isEdit) {
      if (!form.slug?.trim()) {
        enqueueSnackbar(t("tenantModal.validation.slugRequired", "Slug je povinný."), {
          variant: "error",
        });
        return;
      }
      if (!form.admin_password) {
        enqueueSnackbar(
          t("tenantModal.validation.adminPasswordRequired", "Heslo administrátora je povinné."),
          { variant: "error" }
        );
        return;
      }
      if (form.admin_password !== form.admin_password2) {
        enqueueSnackbar(t("tenantModal.validation.passwordsDoNotMatch", "Heslá sa nezhodujú."), {
          variant: "error",
        });
        return;
      }
    }

    setSaving(true);
    try {
      if (!isEdit) {
        await createTenant({
          name: form.name,
          slug: form.slug,
          description: form.description,
          is_active: form.is_active,
          account_type: form.account_type,
          payment_method: form.payment_method,

          anon_validation: form.anon_validation,
          anon_dicom: form.anon_dicom,

          ico: form.ico,
          dic: form.dic,
          ic_dph: form.ic_dph,

          contact_company_address: form.contact_company_address,
          contact_company_postal_code: form.contact_company_postal_code,
          contact_company_town: form.contact_company_town,
          contact_company_country: form.contact_company_country,

          contact_sales_first_name: form.contact_sales_first_name,
          contact_sales_last_name: form.contact_sales_last_name,
          contact_sales_phone: form.contact_sales_phone,
          contact_sales_email: form.contact_sales_email,

          contact_admin_first_name: form.contact_admin_first_name,
          contact_admin_last_name: form.contact_admin_last_name,
          contact_admin_phone: form.contact_admin_phone,
          contact_admin_email: form.contact_admin_email,

          admin_username: adminUsername,
          admin_password: form.admin_password,
        });

        enqueueSnackbar(t("tenantModal.snackbar.tenantCreated", "Tenant bol vytvorený."), {
          variant: "success",
        });
        onChanged?.();
        onClose?.();
        return;
      }

      // Public fields + branding
      if (files.logo || files.bg) {
        await updateTenantPublicWithFiles(tenantId, {
          address: form.address,
          phone: form.phone,
          email: form.email,
          ...(files.logo ? { logo_file: files.logo } : {}),
          ...(files.bg ? { login_photo_file: files.bg } : {}),
        });

        setPreview((p) => ({
          ...p,
          ...(files.logo ? { logo: getTenantLogoUrl(tenantId) + `?t=${Date.now()}` } : {}),
          ...(files.bg ? { bg: getTenantBackgroundUrl(tenantId) + `?t=${Date.now()}` } : {}),
        }));

        Object.values(preview).forEach((u) => u?.startsWith("blob:") && URL.revokeObjectURL(u));
        setFiles({ logo: null, bg: null });
      } else {
        await updateTenantPublic(tenantId, {
          address: form.address,
          phone: form.phone,
          email: form.email,
        });
      }

      // Core tenant fields
      await patchTenant(tenantId, {
        name: form.name,
        slug: form.slug,
        description: form.description,

        is_active: form.is_active,
        account_type: form.account_type,
        payment_method: form.payment_method,

        anon_validation: form.anon_validation,
        anon_dicom: form.anon_dicom,

        ico: form.ico,
        dic: form.dic,
        ic_dph: form.ic_dph,

        contact_company_address: form.contact_company_address,
        contact_company_postal_code: form.contact_company_postal_code,
        contact_company_town: form.contact_company_town,
        contact_company_country: form.contact_company_country,

        contact_sales_first_name: form.contact_sales_first_name,
        contact_sales_last_name: form.contact_sales_last_name,
        contact_sales_phone: form.contact_sales_phone,
        contact_sales_email: form.contact_sales_email,

        contact_admin_first_name: form.contact_admin_first_name,
        contact_admin_last_name: form.contact_admin_last_name,
        contact_admin_phone: form.contact_admin_phone,
        contact_admin_email: form.contact_admin_email,
      });

      enqueueSnackbar(t("tenantModal.snackbar.saved", "Zmeny boli uložené."), { variant: "success" });
      onChanged?.();
      onClose?.();
    } catch (e) {
      enqueueSnackbar(t("tenantModal.snackbar.failedToSave", "Nepodarilo sa uložiť zmeny."), {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      await deleteTenant(tenantId);
      enqueueSnackbar(t("tenantModal.snackbar.tenantDeleted", "Tenant bol odstránený."), {
        variant: "success",
      });
      onChanged?.();
      onClose?.();
    } catch (e) {
      enqueueSnackbar(t("tenantModal.snackbar.failedToDelete", "Nepodarilo sa odstrániť tenant."), {
        variant: "error",
      });
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  const handleActivate = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      const updated = await activateTenant(tenantId);
      enqueueSnackbar(t("tenantModal.snackbar.activated", "Organizácia bola aktivovaná."), { variant: "success" });
      setForm((p) => ({
        ...p,
        is_active: !!updated.is_active,
        deactivated_at: updated.deactivated_at ?? null,
        deactivation_reason: updated.deactivation_reason || "",
      }));
      onChanged?.();
    } catch (e) {
      enqueueSnackbar(
        t("tenantModal.snackbar.failedToActivate", "Nepodarilo sa aktivovať tenant."),
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (reason) => {
    if (!tenantId) return;
    setSaving(true);
    try {
      const updated = await deactivateTenant(tenantId, reason);
      enqueueSnackbar(t("tenantModal.snackbar.deactivated", "Organizácia bola deaktivovaná."), {
        variant: "success",
      });
      setForm((p) => ({
        ...p,
        is_active: !!updated.is_active,
        deactivated_at: updated.deactivated_at ?? null,
        deactivation_reason: updated.deactivation_reason || "",
      }));
      onChanged?.();
    } catch (e) {
      enqueueSnackbar(
        t("tenantModal.snackbar.failedToDeactivate", "Nepodarilo sa deaktivovať tenant."),
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSetTrialExpiration = async (dt) => {
    if (!tenantId) return;
    setSaving(true);
    try {
      const updated = await setTenantTrialExpiration(tenantId, dt);

      enqueueSnackbar(
        t("tenantModal.snackbar.saved", "Zmeny boli uložené."),
        { variant: "success" }
      );

      setForm((p) => ({
        ...p,
        trial_expires_at: updated.trial_expires_at ?? p.trial_expires_at,
        is_active: updated.is_active ?? p.is_active,
        deactivated_at: updated.deactivated_at ?? null,
        deactivation_reason: updated.deactivation_reason || "",
      }));

      onChanged?.();
    } catch (e) {
      enqueueSnackbar(
        t("tenantModal.snackbar.failedToSave", "Nepodarilo sa uložiť zmeny."),
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const loadStats = async (days) => (tenantId ? getTenantStats(tenantId, days) : null);
  const loadLogs = async (params) => (tenantId ? getTenantLogs(tenantId, params) : null);
  const loadAnonymizationStats = async (days) => {
    if (!tenantId) return null;

    // backend supports created_at__gte / created_at__lte
    const createdAtGte = makeCreatedAtGte(days);

    return await getAnonymizationRunStats({
      tenantId,              // superadmin override param
      createdAtGte,          // "YYYY-MM-DD"
      // optionally add createdAtLte: toYyyyMmDd(new Date())
    });
  };

  const tabs = useMemo(() => {
  if (!isEdit) {
    return [
      { key: "general", label: t("tenantModal.tabs.general", "Všeobecné") },
      { key: "contacts", label: t("tenantModal.tabs.contacts", "Kontakty") },
      { key: "admin", label: t("tenantModal.tabs.admin", "Administrátor") },
    ];
  }

  // EDIT MODE (IMPORTANT ORDER must match TabPanel indices in TenantModal.jsx)
  return [
    { key: "general", label: t("tenantModal.tabs.general", "Všeobecné") },
    { key: "contacts", label: t("tenantModal.tabs.contacts", "Kontakty") },
    { key: "branding", label: t("tenantModal.tabs.branding", "Branding") },
    { key: "access", label: t("tenantModal.tabs.access", "Prístup") },

    // ✅ NEW TAB
    { key: "users", label: t("tenantModal.tabs.users", "Používatelia") },

    // shifted down by 1
    { key: "usage", label: t("tenantModal.tabs.usage", "Používanie") },
    { key: "danger", label: t("tenantModal.tabs.danger", "Nebezpečná zóna") },
  ];
}, [isEdit, t]);

  return {
    t,
    isEdit,
    tab,
    setTab,
    loading,
    saving,
    form,
    setForm,
    preview,
    files,
    adminUsername,

    confirmDelete,
    setConfirmDelete,

    tabs,

    onPick,
    removeImg,
    saveAll,
    doDelete,
    handleActivate,
    handleDeactivate,
    handleSetTrialExpiration,
    loadStats,
    loadLogs,
    loadAnonymizationStats,
  };
}
